import { FC, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
} from '@mui/material'

export interface ClientFormData {
  name: string
  email: string
  phone: string
}

export interface ClientFormModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: ClientFormData) => Promise<void>
  initialData?: Partial<ClientFormData>
  title: string
  submitText: string
  isLoading?: boolean
}

export const ClientFormModal: FC<ClientFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  initialData = {},
  title,
  submitText,
  isLoading = false,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm<ClientFormData>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
    },
    mode: 'onChange',
  })

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      const formValues = {
        name: initialData?.name || '',
        email: initialData?.email || '',
        phone: initialData?.phone || '',
      }
      reset(formValues)
      clearErrors()
    }
  }, [open, initialData?.name, initialData?.email, initialData?.phone])

  const handleFormSubmit = async (data: ClientFormData) => {
    try {
      // Transform data to omit empty strings for optional fields
      const transformedData: Partial<ClientFormData> = {
        name: data.name, // Name is always required
      }

      // Only include email if it's not empty
      if (data.email && data.email.trim()) {
        transformedData.email = data.email.trim()
      }

      // Only include phone if it's not empty
      if (data.phone && data.phone.trim()) {
        transformedData.phone = data.phone.trim()
      }

      await onSubmit(transformedData as ClientFormData)
      onClose()
    } catch (error) {
      // Set a general form error
      setError('root', {
        type: 'manual',
        message: error instanceof Error ? error.message : 'An error occurred',
      })
    }
  }

  const handleClose = () => {
    reset()
    clearErrors()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {errors.root && <Alert severity="error">{errors.root.message}</Alert>}

            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Name is required',
                minLength: {
                  value: 1,
                  message: 'Name must not be empty',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  required
                  autoFocus
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              rules={{
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Invalid email format',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone"
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  fullWidth
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isLoading || isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading || isSubmitting}>
            {isSubmitting ? 'Saving...' : submitText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
